import React, { useState, useEffect } from "react";
import { AlertTriangle, CheckCircle2, Coins, MessageSquare, Sparkles, Upload, X, Image as ImageIcon } from "lucide-react";
import { Modal } from "./Modal";
import * as api from "@/src/api/sdk.gen";
import { apiFetch } from "@/src/lib/api-fetch";
import type { WorkspaceCustomCourseRequestItem } from "../../types";
import { getErrorMessage } from "../../types";
import { useOrgDashboard } from "../../context/OrgDashboardContext";

export interface CustomCourseRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  workspace: { slug: string; name: string } | null;
  workspaceDocuments: Array<{ id: number; title: string; document_type: string }>;
  onSuccess: () => Promise<void>;
  initialData?: WorkspaceCustomCourseRequestItem | null;
}

export const CustomCourseRequestModal: React.FC<CustomCourseRequestModalProps> = ({
  isOpen,
  onClose,
  slug,
  workspace,
  workspaceDocuments,
  onSuccess,
  initialData,
}) => {
  const { creditsSummary, workspaces, loadWorkspaces, documents, loadDocuments } = useOrgDashboard();

  const [courseTitle, setCourseTitle] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [learningObjectives, setLearningObjectives] = useState("");
  const [suggestedModulesText, setSuggestedModulesText] = useState("");
  const [selectedDocIds, setSelectedDocIds] = useState<number[]>([]);
  const [imageUrl, setImageUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const [selectedWorkspaceSlug, setSelectedWorkspaceSlug] = useState<string>("");
  const [fetchedWsDocs, setFetchedWsDocs] = useState<Array<{ id: number; title: string; document_type: string }>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submittedSuccess, setSubmittedSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Eagerly fetch documents & workspaces on open if not loaded yet (Issues 15 & 35)
  useEffect(() => {
    if (isOpen) {
      void loadDocuments(true);
      if (!workspaces || workspaces.length === 0) {
        void loadWorkspaces();
      }
    }
  }, [isOpen, workspaces, loadDocuments, loadWorkspaces]);

  // Sync default workspace
  useEffect(() => {
    if (workspace?.slug) {
      setSelectedWorkspaceSlug(workspace.slug);
    } else if (workspaces && workspaces.length > 0 && !selectedWorkspaceSlug) {
      setSelectedWorkspaceSlug(workspaces[0].slug);
    }
  }, [workspace, workspaces, selectedWorkspaceSlug]);

  // Load workspace-scoped documents whenever modal opens or selected workspace changes
  useEffect(() => {
    const wsSlugToLoad = workspace?.slug || selectedWorkspaceSlug;
    if (!isOpen || !slug || !wsSlugToLoad) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.listWorkspaceDocumentsApiV1B2bSlugWorkspacesWsSlugDocumentsGet({
          path: { slug, ws_slug: wsSlugToLoad },
        });
        if (!cancelled && data) {
          const docs = ((data as any).documents || []).map((d: any) => ({
            id: d.id,
            title: d.title,
            document_type: d.document_type || "SOP",
          }));
          setFetchedWsDocs(docs);
        }
      } catch {
        // Fallback to org documents
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, slug, workspace?.slug, selectedWorkspaceSlug]);

  const resetForm = () => {
    setCourseTitle("");
    setTargetAudience("");
    setLearningObjectives("");
    setSuggestedModulesText("");
    setSelectedDocIds([]);
    setImageUrl("");
    setImageUploadError(null);
    setError(null);
    setSubmittedSuccess(false);
  };

  useEffect(() => {
    if (initialData) {
      setCourseTitle(initialData.course_title || "");
      setTargetAudience(initialData.target_audience || "");
      setLearningObjectives(initialData.learning_objectives || "");
      setSuggestedModulesText((initialData.suggested_modules || []).join("\n"));
      setSelectedDocIds(initialData.reference_document_ids || []);
      setImageUrl(initialData.image_url || "");
      setError(null);
      setSubmittedSuccess(false);
    } else {
      resetForm();
    }
  }, [initialData, isOpen]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const effectiveWsSlug = workspace?.slug || selectedWorkspaceSlug;
  const currentWorkspaceName =
    workspace?.name || workspaces?.find((w) => w.slug === effectiveWsSlug)?.name;

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !slug || !effectiveWsSlug) return;

    try {
      setUploadingImage(true);
      setImageUploadError(null);

      const formData = new FormData();
      formData.append("file", file);

      const res = await apiFetch(
        `/api/v1/b2b/${slug}/workspaces/${effectiveWsSlug}/custom-course-requests/upload-image`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.image_url) {
        setImageUploadError(data?.detail || "Failed to upload course image.");
      } else {
        setImageUrl(data.image_url);
      }
    } catch (err: any) {
      setImageUploadError(err?.message || "Failed to upload image file.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug || !effectiveWsSlug || !courseTitle.trim() || !targetAudience.trim() || !learningObjectives.trim()) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const modules = suggestedModulesText
        .split("\n")
        .map((m) => m.trim())
        .filter(Boolean);

      if (initialData) {
        const { data, error: apiError } = await api.updateWorkspaceCustomCourseRequestApiV1B2bSlugWorkspacesWsSlugCustomCourseRequestsRequestIdPatch({
          path: { slug, ws_slug: effectiveWsSlug, request_id: initialData.id },
          body: {
            course_title: courseTitle.trim(),
            target_audience: targetAudience.trim(),
            learning_objectives: learningObjectives.trim(),
            suggested_modules: modules,
            reference_document_ids: selectedDocIds,
            image_url: imageUrl.trim() || undefined,
            resubmit: true,
          },
        });

        if (data) {
          setSubmittedSuccess(true);
          await onSuccess();
        } else {
          setError(getErrorMessage(apiError, "Failed to update course request"));
        }
      } else {
        const { data, error: apiError } = await api.createWorkspaceCustomCourseRequestApiV1B2bSlugWorkspacesWsSlugCustomCourseRequestsPost({
          path: { slug, ws_slug: effectiveWsSlug },
          body: {
            course_title: courseTitle.trim(),
            target_audience: targetAudience.trim(),
            learning_objectives: learningObjectives.trim(),
            suggested_modules: modules,
            reference_document_ids: selectedDocIds,
            image_url: imageUrl.trim() || undefined,
          },
        });

        if (data) {
          setSubmittedSuccess(true);
          await onSuccess();
        } else {
          setError(getErrorMessage(apiError, "Failed to submit course request"));
        }
      }
    } catch (err) {
      console.error("Error submitting custom course request:", err);
      setError("Network error submitting course request");
    } finally {
      setSubmitting(false);
    }
  };

  const currentCredits = creditsSummary?.ai_credits_balance ?? 0;

  // Combine workspaceDocuments prop, fetchedWsDocs, and context documents (deduplicated by id)
  const availableDocuments = React.useMemo(() => {
    const map = new Map<number, { id: number; title: string; document_type: string }>();
    for (const d of workspaceDocuments || []) {
      map.set(d.id, { id: d.id, title: d.title, document_type: d.document_type || "SOP" });
    }
    for (const d of fetchedWsDocs || []) {
      map.set(d.id, { id: d.id, title: d.title, document_type: d.document_type || "SOP" });
    }
    for (const d of documents || []) {
      map.set(d.id, { id: d.id, title: d.title, document_type: d.document_type || "SOP" });
    }
    return Array.from(map.values());
  }, [workspaceDocuments, fetchedWsDocs, documents]);

  if (submittedSuccess) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Request Submitted"
        icon={<CheckCircle2 className="w-5 h-5 text-emerald-600" />}
        maxWidth="max-w-md"
      >
        <div className="text-center py-4 space-y-4">
          <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto ring-8 ring-emerald-50/50">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <div className="space-y-2">
            <h3 className="text-base font-bold text-slate-900">
              {initialData ? "Request Updated Successfully" : "Request Submitted Successfully"}
            </h3>
            <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-900 text-xs font-semibold leading-relaxed">
              Your custom course request has been successfully submitted to the Super Admin.
            </div>
            <p className="text-xs text-slate-500 leading-relaxed px-2">
              Our curriculum team will review your requirements and syllabus. You can monitor its status and super admin feedback anytime from your workspace.
            </p>
          </div>
          <div className="pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition shadow-sm cursor-pointer"
            >
              Done
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={initialData ? "Revise Custom Course Request" : "Request Custom Training Program"}
      subtitle={currentWorkspaceName ? `For workspace: ${currentWorkspaceName}` : undefined}
      icon={<Sparkles className="w-5 h-5 text-purple-600" />}
      maxWidth="max-w-lg"
    >
      {error && (
        <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs font-semibold">
          {error}
        </div>
      )}

      {/* Admin Feedback Banner (when revising) */}
      {initialData?.admin_feedback && (
        <div className="mb-4 p-3.5 bg-amber-50 border border-amber-200 rounded-2xl text-xs space-y-1.5">
          <div className="flex items-center gap-1.5 font-bold text-amber-900">
            <MessageSquare className="w-4 h-4 text-amber-600 shrink-0" />
            <span>Super Admin Feedback to Address:</span>
          </div>
          <p className="text-amber-800 leading-relaxed pl-5 whitespace-pre-wrap">
            {initialData.admin_feedback}
          </p>
        </div>
      )}

      {/* Credit Balance & Policy Header */}
      <div className="mb-4 space-y-2">
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-slate-800 font-semibold">
            <Coins className="w-4 h-4 text-amber-500" />
            <span>Org AI Credits: <strong className="font-mono text-indigo-700">{currentCredits}</strong></span>
          </div>
          <span className="text-[11px] text-slate-500">Curated by Inara (~10 credits/lesson)</span>
        </div>

        {currentCredits <= 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl text-[11px] flex items-start gap-2 leading-relaxed">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <span>
              Your organization currently has 0 AI credits. You can submit this request for syllabus review now, but you will need to purchase AI credits in the <strong>Billing</strong> tab before the course can be generated by our curriculum team.
            </span>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        {/* Workspace Dropdown if not preselected */}
        {!workspace && (
          <div>
            <label className="block font-semibold text-slate-700 mb-1">Target Workspace *</label>
            {workspaces && workspaces.length > 0 ? (
              <select
                value={selectedWorkspaceSlug}
                onChange={(e) => setSelectedWorkspaceSlug(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500 text-xs"
              >
                {workspaces.map((ws) => (
                  <option key={ws.slug} value={ws.slug}>
                    {ws.name} ({ws.member_count} members)
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-amber-700 text-xs">
                No workspace found. Please configure a workspace first.
              </p>
            )}
          </div>
        )}

        <div>
          <label className="block font-semibold text-slate-700 mb-1">Course Title *</label>
          <input
            type="text"
            required
            placeholder="e.g. Senior Backend Architecture & Incident Response"
            value={courseTitle}
            onChange={(e) => setCourseTitle(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
          />
        </div>

        {/* Course Cover Image Upload / URL (Issue 13) */}
        <div>
          <label className="block font-semibold text-slate-700 mb-1">
            Course Cover Image (Optional)
          </label>
          {imageUrl ? (
            <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 max-h-48 group">
              <img
                src={imageUrl}
                alt="Course cover preview"
                className="w-full h-36 object-cover"
                onError={() => setImageUrl("")}
              />
              <button
                type="button"
                onClick={() => setImageUrl("")}
                className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-lg transition"
                title="Remove Image"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <label className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl border border-slate-200 flex items-center gap-1.5 cursor-pointer transition shrink-0">
                  <Upload className="w-3.5 h-3.5 text-indigo-600" />
                  <span>{uploadingImage ? "Uploading..." : "Upload Image"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingImage || !effectiveWsSlug}
                    onChange={handleImageUpload}
                  />
                </label>
                <input
                  type="url"
                  placeholder="Or enter image URL (https://...)"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500 text-xs"
                />
              </div>
              {imageUploadError && (
                <p className="text-[11px] text-rose-600 font-semibold">{imageUploadError}</p>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1">Target Audience *</label>
          <input
            type="text"
            required
            placeholder="e.g. Mid-to-Senior Engineers, Sales SDRs, New Hires"
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
          />
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1">Learning Objectives & Outcomes *</label>
          <textarea
            rows={3}
            required
            placeholder="What specific skills, compliance procedures, or company practices should learners master?"
            value={learningObjectives}
            onChange={(e) => setLearningObjectives(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500 resize-none"
          />
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1">
            Suggested Modules (One per line)
          </label>
          <textarea
            rows={3}
            placeholder={"Module 1: Code Review Guidelines\nModule 2: Cloud Infrastructure & Secrets\nModule 3: Runbook Execution"}
            value={suggestedModulesText}
            onChange={(e) => setSuggestedModulesText(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500 font-mono text-[11px] resize-none"
          />
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1.5">
            Attach Reference Documents / SOPs for Grounding:
          </label>
          {availableDocuments.length === 0 ? (
            <p className="text-slate-400 italic text-[11px]">
              No documents currently uploaded to this workspace. Upload SOPs in the &quot;SOPs &amp; Docs&quot; tab to ground generation.
            </p>
          ) : (
            <div className="space-y-1.5 max-h-32 overflow-y-auto border border-slate-200 rounded-xl p-2 bg-slate-50">
              {availableDocuments.map((doc) => {
                const checked = selectedDocIds.includes(doc.id);
                return (
                  <label key={doc.id} className="flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer hover:text-slate-900">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDocIds([...selectedDocIds, doc.id]);
                        } else {
                          setSelectedDocIds(selectedDocIds.filter((id) => id !== doc.id));
                        }
                      }}
                      className="rounded text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="font-medium truncate">{doc.title}</span>
                    <span className="text-slate-400 font-mono text-[10px]">({doc.document_type})</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || !effectiveWsSlug || !courseTitle.trim() || !targetAudience.trim() || !learningObjectives.trim()}
            className="px-5 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold rounded-xl shadow-sm transition disabled:opacity-50 cursor-pointer"
          >
            {submitting
              ? initialData
                ? "Updating..."
                : "Submitting..."
              : initialData
              ? "Update & Resubmit Request"
              : "Submit Course Request"}
          </button>
        </div>
      </form>
    </Modal>
  );
};
